import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { notifications } from "@mantine/notifications";
import {
  physicianReportDeliveryService,
  CreatePhysicianReportDeliveryInput,
  UpdatePhysicianReportDeliveryInput,
} from "../../services/admin/physicianreportdelivery.service";

// Component-handled error: the modal renders an inline "set up portal access first"
// alert instead of the default red toast.
function isNoPortalAccessError(error: unknown): boolean {
  const response = (
    error as { response?: { status?: number; data?: { error?: string } } }
  )?.response;
  return (
    response?.status === 409 && response?.data?.error === "NO_PORTAL_ACCESS"
  );
}

export function usePhysicianReportDeliveryAdmin() {
  return useQuery({
    queryKey: ["physicianReportDelivery", "admin"],
    queryFn: () => physicianReportDeliveryService.getAll(),
  });
}

export function usePhysicianReportDeliveryByPhysician(
  physicianId: string | null
) {
  return useQuery({
    queryKey: ["physicianReportDelivery", "byPhysician", physicianId],
    queryFn: () =>
      physicianReportDeliveryService.getByPhysicianId(physicianId!),
    enabled: !!physicianId,
  });
}

export function useCreatePhysicianReportDelivery() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreatePhysicianReportDeliveryInput) =>
      physicianReportDeliveryService.create(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["physicianReportDelivery"] });
      notifications.show({
        title: "Success",
        message: "Physician report delivery created successfully",
        color: "green",
      });
    },
    onError: (error: any) => {
      if (isNoPortalAccessError(error)) {
        return;
      }
      notifications.show({
        title: "Error",
        message: error.message || "Failed to create physician report delivery",
        color: "red",
      });
    },
  });
}

export function useUpdatePhysicianReportDelivery() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: UpdatePhysicianReportDeliveryInput) =>
      physicianReportDeliveryService.update(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["physicianReportDelivery"] });
      notifications.show({
        title: "Success",
        message: "Physician report delivery updated successfully",
        color: "green",
      });
    },
    onError: (error: any) => {
      if (isNoPortalAccessError(error)) {
        return;
      }
      notifications.show({
        title: "Error",
        message: error.message || "Failed to update physician report delivery",
        color: "red",
      });
    },
  });
}

export function useTogglePhysicianReportDeliveryActive() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => physicianReportDeliveryService.toggleActive(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["physicianReportDelivery"] });
      notifications.show({
        title: "Success",
        message: "Delivery method status updated successfully",
        color: "green",
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to update delivery method status",
        color: "red",
      });
    },
  });
}

export function useDeletePhysicianReportDelivery() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => physicianReportDeliveryService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["physicianReportDelivery"] });
      notifications.show({
        title: "Success",
        message: "Physician report delivery deleted successfully",
        color: "green",
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to delete physician report delivery",
        color: "red",
      });
    },
  });
}
